import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	BilletsBetweenCobblesPickerCrew,
	BilletsBetweenCobblesPickerProductType,
	BilletsBetweenCobblesPickerReportType,
	BilletsBetweenCobblesPickers,
	BilletsBetweenCobblesRow,
	BilletsBetweenCobblesSortType,
} from "../models/BilletsBetweenCobbles";

export interface GetBilletsBetweenCobblesParams {
	start: string;
	end: string;
	crewId?: string | null;
	productTypeId?: number | null;
	reportType?: number;
	sortType?: BilletsBetweenCobblesSortType | null;
	signal?: AbortSignal;
}

export const getBilletsBetweenCobblesData = async ({
	start,
	end,
	crewId = null,
	productTypeId = null,
	reportType = 1,
	sortType = null,
	signal,
}: GetBilletsBetweenCobblesParams): Promise<BilletsBetweenCobblesRow[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Start", value: start },
		{ name: "End", value: end },
		{ name: "Crew", value: crewId },
		{ name: "ProductTypeId", value: productTypeId },
		{ name: "SortType", value: sortType },
		{ name: "ReportType", value: reportType },
	];

	const spName = "[KPI].[GetBilletsBetweenCobblesDataWeb]";
	const resp = await apiService.callV2(spName, parameters, signal);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error fetching billets between cobbles data",
		);
	}
	return get(resp, "data.tables[0].rows", []) as BilletsBetweenCobblesRow[];
};

export interface GetBilletsBetweenCobblesPickersParams {
	start: string;
	end: string;
	signal?: AbortSignal;
}

export const getBilletsBetweenCobblesPickers = async ({
	start,
	end,
	signal,
}: GetBilletsBetweenCobblesPickersParams): Promise<BilletsBetweenCobblesPickers> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
		{ name: "onlyActives", value: true },
	];

	const spName = "[KPI].[GetBilletsBetweenCobblesPickersDataWeb]";
	const resp = await apiService.callV2(spName, parameters, signal);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error fetching billets between cobbles pickers",
		);
	}

	// The pickers SP returns 4 result sets in a fixed order:
	//   tables[0] = meta { Index, Name } — informational only
	//   tables[1] = crews (CREWS)
	//   tables[2] = product types (PRODUCTTYPES)
	//   tables[3] = report types (REPORTTYPE)
	const crews = get(
		resp,
		"data.tables[1].rows",
		[],
	) as BilletsBetweenCobblesPickerCrew[];
	const productTypes = get(
		resp,
		"data.tables[2].rows",
		[],
	) as BilletsBetweenCobblesPickerProductType[];
	const reportTypes = get(
		resp,
		"data.tables[3].rows",
		[],
	) as BilletsBetweenCobblesPickerReportType[];

	return { crews, productTypes, reportTypes };
};
